import styled from 'styled-components'
import { FormInput } from 'components/Form/Core/styled'

export const DesktopTable = styled.div`
  display: block;

  .MuiTableCell-head {
    border-color: ${({ theme }) => theme.palette.colors.borderSoft};
  }

  @media (max-width: 767px) {
    display: none;
  }
`

export const MobileCards = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: block;
    min-width: 0;
    overflow: hidden;

    > div,
    .MuiPaper-root {
      max-width: 100%;
      min-width: 0;
      width: 100%;
    }

    .MuiPaper-root > div {
      min-width: 0;
      white-space: normal;
    }

    .MuiPaper-root > div > * {
      max-width: 100%;
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
`

export const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  .MuiButton-outlinedError {
    border-color: ${({ theme }) => theme.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark};
    color: ${({ theme }) => theme.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark};

    &:hover {
      background: ${({ theme }) => theme.palette.colors.controlSurface};
    }
  }

  .MuiButton-root.Mui-disabled {
    background: ${({ theme }) => theme.palette.colors.disabledBackground};
    border-color: ${({ theme }) => theme.palette.colors.borderStrong};
    color: ${({ theme }) => theme.palette.colors.disabledText};
  }

  .MuiButton-root:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.colors.text};
    outline-offset: 3px;
  }

  @media (max-width: 767px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;

    .MuiButton-root {
      width: 100% !important;
    }
  }
`

export const CompanyCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 180px;
`

export const ReviewBadge = styled.span`
  align-self: flex-start;
  background: ${({ theme }) => theme.palette.colors.warningMuted};
  border: 1px solid ${({ theme }) => theme.palette.warning.main};
  border-radius: 999px;
  color: ${({ theme }) => theme.palette.colors.text};
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
`

export const ReviewExplanation = styled.span`
  color: ${({ theme }) => theme.palette.colors.muted};
  font-size: 12px;
  line-height: 1.4;
  max-width: 260px;
  white-space: normal;
`

export const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${FormInput} {
    max-width: 100%;
  }
`

export const ReviewDetails = styled.div`
  background: ${({ theme }) => theme.palette.colors.surfaceElevated};
  border: 1px solid ${({ theme }) => theme.palette.colors.borderSoft};
  border-radius: 12px;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 16px;

  div {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  span {
    color: ${({ theme }) => theme.palette.colors.subdued};
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }

  strong {
    color: ${({ theme }) => theme.palette.colors.text};
    overflow-wrap: anywhere;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

export const OrganizationVerificationPanel = styled.section`
  background: ${({ theme }) => theme.palette.colors.warningMuted};
  border: 1px solid ${({ theme }) => theme.palette.warning.main};
  border-left-width: 4px;
  border-radius: 10px;
  color: ${({ theme }) => theme.palette.colors.text};
  padding: 18px 20px;

  h2,
  h3,
  p {
    margin: 0;
  }

  h2 {
    font-size: 17px;
    line-height: 1.35;
  }

  h3 {
    font-size: 15px;
    line-height: 1.4;
    margin-top: 18px;
  }

  p,
  li {
    color: ${({ theme }) => theme.palette.colors.muted};
    font-size: 14px;
    line-height: 1.55;
  }

  h2 + p {
    margin-top: 10px;
  }

  p + p {
    margin-top: 8px;
  }

  ol {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 12px 0 0;
    padding-left: 22px;
  }

  li {
    padding-left: 2px;
  }

  li strong,
  .important strong {
    color: ${({ theme }) => theme.palette.colors.text};
  }

  li p {
    margin-top: 3px;
  }

  .important {
    border-top: 1px solid ${({ theme }) => theme.palette.colors.borderSoft};
    margin-top: 16px;
    padding-top: 14px;
  }

  @media (max-width: 600px) {
    padding: 16px;

    h2 {
      font-size: 16px;
    }
  }
`

export const ConfirmationNote = styled.p`
  color: ${({ theme }) => theme.palette.colors.muted};
  line-height: 1.5;
  margin: 0;

  strong {
    color: ${({ theme }) => theme.palette.colors.text};
  }
`

export const ErrorText = styled.p`
  color: ${({ theme }) => theme.palette.error.main};
  font-size: 13px;
  margin: -12px 0 0;
`

export const DialogActions = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 600px) {
    .MuiButton-root {
      flex: 1;
    }
  }
`

export const DecisionState = styled.div`
  align-items: center;
  border: 1px solid ${({ approved, theme }) => approved
    ? theme.palette.success.main
    : theme.palette.error.main};
  border-radius: 999px;
  color: ${({ approved, theme }) => approved
    ? (theme.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark)
    : (theme.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark)};
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  gap: 8px;
  justify-content: center;
  min-height: 32px;
  min-width: 196px;
  padding: 6px 12px;
`

export const EmailLink = styled.a`
  color: ${({ theme }) => theme.palette.colors.text};
  overflow-wrap: anywhere;
  text-decoration-color: ${({ theme }) => theme.palette.blue.main};
  text-underline-offset: 3px;

  &:hover {
    color: ${({ theme }) => theme.palette.colors.muted};
    text-decoration-color: currentColor;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.colors.text};
    outline-offset: 3px;
  }
`

export const StatusBadge = styled.span`
  background: ${({ theme }) => theme.palette.colors.warningMuted};
  border: 1px solid ${({ theme }) => theme.palette.warning.main};
  border-radius: 999px;
  color: ${({ theme }) => theme.palette.colors.text};
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 10px;
`

export const Pagination = styled.div`
  border-top: 1px solid ${({ theme }) => theme.palette.colors.borderSoft};
  color: ${({ theme }) => theme.palette.colors.text};
  margin-top: 8px;
  padding-top: 8px;
`
